import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Chip, Typography, IconButton, TextField, MenuItem,
  Alert, Snackbar, CircularProgress, Paper, Grid, Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CropIcon from '@mui/icons-material/Crop';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import useIsMobile from '../hooks/useIsMobile';
import RecadragePhotoDialog from './RecadragePhotoDialog';
import {
  DossierNormes, PieceDossier, OrigineNorme, Conformite, RectNormalise,
  chargerDossierNormes, convertirPieceDossier, convertirToutDossier, urlApercu, urlTelechargement,
  formaterTaille, messageErreur, outilsManquants, peutRecadrer, cadreRepris, ratioRegle
} from '../services/normesPjServices';

// Fenêtre « Normes PJ » d'une demande : pièces du dossier (photo et passeport du voyageur, PJ du formulaire) face à la
// norme de son visa, conversion en versions conformes (demandes/<chemin>/conformes/) et téléchargement sous un nom
// prêt à déposer ; « Recadrer » ouvre l'outil de recadrage manuel. Les originaux ne sont jamais modifiés.

interface Props {
  open: boolean;
  chemin: string;
  onClose: () => void;
  // false : admin sans menu Pièces jointes (admin Legrand), les normes restent gérées dans l'admin VisaMonde
  menuNormes?: boolean;
}

const ORIGINES: Record<OrigineNorme, { label: string; color: 'success' | 'info' | 'default' | 'warning' }> = {
  visa: { label: 'trouvée par le visa du dossier', color: 'success' },
  mots_cles: { label: 'trouvée par les mots-clés', color: 'info' },
  manuel: { label: 'choisie à la main', color: 'default' },
  aucune: { label: 'aucune norme trouvée', color: 'warning' },
};

const titrePiece = (p: PieceDossier): string => {
  if (p.origine === 'voyageur') return p.cle === 'photo' ? 'Photo du voyageur' : 'Passeport du voyageur';
  return p.nom || p.cle;
};

// « JPG · 279 Ko · 1000×1000 px » ; « PDF · 250 Ko · 3 pages »
const mesures = (format: string | undefined, taille: number | undefined, largeur?: number | null, hauteur?: number | null, pages?: number | null): string =>
  [
    format || '',
    taille !== undefined ? formaterTaille(taille) : '',
    largeur && hauteur ? `${largeur}×${hauteur} px` : '',
    pages ? `${pages} page${pages > 1 ? 's' : ''}` : '',
  ].filter(Boolean).join(' · ');

// Pièce à convertir par « Tout convertir » : même filtre que POST /convertir-tout
const aConvertir = (p: PieceDossier): boolean => p.present && !!p.regle && p.convertible && p.conformite.ok === false
  && !(p.conversion && !p.conversion.perimee && p.conversion.conformite.ok);

// Pièce prête à déposer : conforme telle quelle, ou version conforme à jour
const prete = (p: PieceDossier): boolean => p.conformite.ok === true
  || !!(p.conversion && !p.conversion.perimee && p.conversion.conformite.ok);

const ChipConformite: React.FC<{ c: Conformite }> = ({ c }) => {
  if (c.ok === true) return <Chip size="small" color="success" label="Conforme" />;
  if (c.ok === false) return <Chip size="small" color="error" label="Non conforme" />;
  return <Chip size="small" variant="outlined" label="Hors norme" />;
};

// Vignette JPEG servie par le backend ; format sans aperçu (Word, ZIP…) ou erreur -> icône
const Vignette: React.FC<{ src: string; lien: string | null; alt: string }> = ({ src, lien, alt }) => {
  const [echec, setEchec] = useState(false);
  useEffect(() => setEchec(false), [src]);
  const contenu = echec
    ? <InsertDriveFileIcon sx={{ fontSize: 56, color: 'text.disabled' }} />
    : <img src={src} alt={alt} onError={() => setEchec(true)} style={{ maxWidth: '100%', maxHeight: 160, objectFit: 'contain', display: 'block' }} />;
  return (
    <Box sx={{ height: 170, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fafafa', border: '1px solid #eee', borderRadius: 1, mb: 1, overflow: 'hidden' }}>
      {lien ? <a href={lien} target="_blank" rel="noopener noreferrer" title="Ouvrir dans un nouvel onglet">{contenu}</a> : contenu}
    </Box>
  );
};

interface CarteProps {
  piece: PieceDossier;
  chemin: string;
  normeId: number | null;
  enCours: boolean;
  onConvertir: (p: PieceDossier, page: number | null, recadrage: RectNormalise | null) => void;
  onRecadrer: (p: PieceDossier, page: number | null) => void;
}

const PieceCarte: React.FC<CarteProps> = ({ piece: p, chemin, normeId, enCours, onConvertir, onRecadrer }) => {
  const c = p.conversion;
  const [page, setPage] = useState(c && c.parametres && c.parametres.page ? c.parametres.page : 1);
  const pages = p.pages || 1;
  // Page d'un PDF converti en image : au choix (1 par défaut)
  const pageAuChoix = p.format === 'pdf' && !!p.regle && p.regle.format_sortie !== 'pdf' && pages > 1;
  const pageEnvoyee = pageAuChoix ? page : null;
  const manuel = c && c.parametres ? c.parametres.recadrage : null;
  // Proportions imposées que l'original ne respecte pas (ou dimensions inconnues : PDF, HEIC) : le cadre automatique a coupé
  const ratio = ratioRegle(p.regle);
  const coupeeAuto = !manuel && ratio !== null && !(p.largeur && p.hauteur && Math.abs(p.largeur / p.hauteur - ratio) <= 0.01 * ratio);
  // « Reconvertir » garde le cadre ajusté à la main (même page, original inchangé)
  const boutonConvertir = (libelle: string, principal: boolean) => (
    <Button
      size="small"
      variant={principal ? 'contained' : 'text'}
      startIcon={enCours ? <CircularProgress size={14} color="inherit" /> : <AutoFixHighIcon />}
      disabled={enCours}
      onClick={() => onConvertir(p, pageEnvoyee, cadreRepris(c, pageEnvoyee))}
    >
      {enCours ? 'Conversion…' : libelle}
    </Button>
  );
  const boutonRecadrer = peutRecadrer(p) && (
    <Button size="small" variant="text" startIcon={<CropIcon />} disabled={enCours} onClick={() => onRecadrer(p, pageEnvoyee)}>
      Recadrer
    </Button>
  );

  return (
    <Paper component="section" aria-label={titrePiece(p)} variant="outlined" sx={{ p: 2, mb: 1.5 }}>
      <Grid container spacing={2}>
        {/* Original */}
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2" noWrap title={titrePiece(p)} sx={{ mb: 0.5 }}>{titrePiece(p)}</Typography>
          {!p.present ? (
            <Box sx={{ height: 170, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fafafa', border: '1px dashed #ccc', borderRadius: 1 }}>
              <Typography color="text.secondary">Aucun fichier</Typography>
            </Box>
          ) : (
            <>
              <Vignette src={urlApercu(chemin, p.id, 'original', p.mtime)} lien={p.url} alt={titrePiece(p)} />
              <Typography variant="body2">{mesures(p.format_libelle, p.taille, p.largeur, p.hauteur, p.pages)}</Typography>
              {p.extension_trompeuse && (
                <Tooltip title="L'extension du fichier ne correspond pas à son contenu : c'est le vrai format qui compte pour la norme.">
                  <Chip size="small" color="warning" variant="outlined" sx={{ mt: 0.5, maxWidth: '100%' }}
                    label={`Nommé « ${p.nom} » mais c'est un ${p.format_libelle}`} />
                </Tooltip>
              )}
              {p.erreur && <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>{p.erreur}</Typography>}
            </>
          )}
        </Grid>

        {/* Norme */}
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Norme{p.regle ? ` : ${p.regle.libelle}` : ''}</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {p.regle ? (p.resume_regle || 'aucune contrainte') : 'Aucune règle de la norme ne s\'applique à cette pièce.'}
          </Typography>
          <ChipConformite c={p.conformite} />
          {p.conformite.raisons.length > 0 && (
            <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
              {p.conformite.raisons.map((r) => (
                <Typography component="li" variant="body2" color="error" key={r}>{r}</Typography>
              ))}
            </Box>
          )}
        </Grid>

        {/* Version conforme */}
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Version conforme</Typography>
          {c ? (
            <>
              <Vignette src={urlApercu(chemin, p.id, 'conforme', c.date_conversion)} lien={c.url} alt="Version conforme" />
              <Typography variant="body2">{mesures(c.format.toUpperCase(), c.taille, c.largeur, c.hauteur, c.pages)}</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', my: 0.5 }}>
                <ChipConformite c={c.conformite} />
                {c.perimee && <Chip size="small" color="warning" label="Original modifié depuis : reconvertir" />}
              </Box>
              {c.conformite.raisons.map((r) => (
                <Typography key={r} variant="caption" color="error" display="block">Règle actuelle : {r}</Typography>
              ))}
              {c.avertissements.map((a) => (
                <Typography key={a} variant="caption" sx={{ color: 'warning.dark' }} display="block">{a}</Typography>
              ))}
              {manuel && <Typography variant="caption" color="text.secondary" display="block">Cadre ajusté à la main.</Typography>}
              {coupeeAuto && peutRecadrer(p) && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Cadre choisi automatiquement : vérifiez la vignette ; s'il ne convient pas, « Recadrer ».
                </Typography>
              )}
              {pageAuChoix && (
                <TextField select size="small" label="Page" value={page} onChange={(e) => setPage(Number(e.target.value))} sx={{ mt: 1, minWidth: 120 }}>
                  {Array.from({ length: pages }, (_, i) => i + 1).map((n) => <MenuItem key={n} value={n}>Page {n} / {pages}</MenuItem>)}
                </TextField>
              )}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                <Button size="small" variant="contained" startIcon={<FileDownloadIcon />}
                  href={urlTelechargement(chemin, p.id, 'conforme', normeId)} target="_blank" rel="noopener noreferrer">
                  Télécharger
                </Button>
                {p.convertible && boutonConvertir('Reconvertir', false)}
                {boutonRecadrer}
              </Box>
            </>
          ) : p.present && p.conformite.ok === true ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Déjà conforme : l'original peut être déposé tel quel.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />}
                  href={urlTelechargement(chemin, p.id, 'original', normeId)} target="_blank" rel="noopener noreferrer">
                  Télécharger l'original
                </Button>
                {boutonRecadrer}
              </Box>
            </>
          ) : p.convertible && p.regle ? (
            <>
              {pageAuChoix && (
                <TextField select size="small" label="Page à convertir" value={page} onChange={(e) => setPage(Number(e.target.value))} sx={{ mb: 1, minWidth: 160 }}>
                  {Array.from({ length: pages }, (_, i) => i + 1).map((n) => <MenuItem key={n} value={n}>Page {n} / {pages}</MenuItem>)}
                </TextField>
              )}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {boutonConvertir(`Convertir en ${p.regle.format_sortie.toUpperCase()}`, true)}
                {boutonRecadrer}
              </Box>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">{p.raison_non_convertible || 'Pièce non convertible.'}</Typography>
          )}
        </Grid>
      </Grid>
    </Paper>
  );
};

const ConformitePjDialog: React.FC<Props> = ({ open, chemin, onClose, menuNormes = true }) => {
  const isMobile = useIsMobile();
  const [data, setData] = useState<DossierNormes | null>(null);
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [normeImposee, setNormeImposee] = useState<number | null>(null); // null = choix automatique
  const [enCours, setEnCours] = useState<string[]>([]);
  const [toutEnCours, setToutEnCours] = useState(false);
  const [recadrage, setRecadrage] = useState<{ piece: PieceDossier; page: number | null } | null>(null); // outil ouvert
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' | 'warning' } | null>(null);

  const charger = useCallback(async (norme: number | null) => {
    setLoading(true);
    setErreur(null);
    try {
      setData(await chargerDossierNormes(chemin, norme));
    } catch (e) {
      console.error('Erreur chargement des pièces du dossier', e);
      setData(null);
      setErreur(messageErreur(e, 'Chargement des pièces impossible (backend injoignable ?)'));
    } finally {
      setLoading(false);
    }
  }, [chemin]);

  // À chaque ouverture : norme choisie automatiquement
  useEffect(() => {
    if (!open || !chemin) return;
    setNormeImposee(null);
    setRecadrage(null);
    setData(null);
    charger(null);
  }, [open, chemin, charger]);

  const changerNorme = (valeur: string) => {
    const id = valeur === 'auto' ? null : Number(valeur);
    setNormeImposee(id);
    charger(id);
  };

  // Norme de la vue affichée : envoyée telle quelle aux conversions et téléchargements
  const normeId = data && data.norme ? data.norme.id : null;

  // Pièce à jour et message après une conversion (boutons de la carte ou outil de recadrage)
  const appliquer = (p: PieceDossier, r: { piece: PieceDossier; copie: boolean }) => {
    setData((prev) => (prev ? { ...prev, pieces: prev.pieces.map((x) => (x.id === r.piece.id ? r.piece : x)) } : prev));
    const conv = r.piece.conversion;
    setToast({
      msg: conv
        ? `${titrePiece(p)} : version conforme ${conv.format.toUpperCase()} de ${formaterTaille(conv.taille)}${r.copie ? ' (copie de l\'original, déjà conforme)' : ''}`
        : `${titrePiece(p)} : conversion faite`,
      sev: 'success',
    });
  };

  const convertir = async (p: PieceDossier, page: number | null, cadre: RectNormalise | null) => {
    setEnCours((prev) => [...prev, p.id]);
    try {
      appliquer(p, await convertirPieceDossier(chemin, cadre ? { piece: p.id, norme: normeId, page, recadrage: cadre } : { piece: p.id, norme: normeId, page }));
    } catch (e) {
      console.error('Erreur conversion', e);
      setToast({ msg: `${titrePiece(p)} : ${messageErreur(e, 'conversion impossible')}`, sev: 'error' });
    } finally {
      setEnCours((prev) => prev.filter((id) => id !== p.id));
    }
  };

  const aFaire = data ? data.pieces.filter(aConvertir) : [];

  const toutConvertir = async () => {
    if (!aFaire.length) return;
    setToutEnCours(true);
    setEnCours(aFaire.map((p) => p.id));
    try {
      const { resultats, ...vue } = await convertirToutDossier(chemin, normeId);
      setData(vue);
      const echecs = resultats.filter((x) => !x.ok);
      setToast(echecs.length
        ? {
          msg: `${resultats.length - echecs.length} pièce(s) convertie(s), ${echecs.length} en échec : `
            + echecs.map((x) => `${x.nom || x.piece} (${x.message})`).join(' ; '),
          sev: 'warning',
        }
        : { msg: `${resultats.length} pièce(s) convertie(s)`, sev: 'success' });
    } catch (e) {
      console.error('Erreur conversion du dossier', e);
      setToast({ msg: messageErreur(e, 'Conversion du dossier impossible'), sev: 'error' });
    } finally {
      setToutEnCours(false);
      setEnCours([]);
    }
  };

  const manques = data ? outilsManquants(data.outils) : [];
  const voyageur = data ? data.pieces.filter((p) => p.origine === 'voyageur') : [];
  const formulaire = data ? data.pieces.filter((p) => p.origine === 'demande') : [];
  const avecRegle = data ? data.pieces.filter((p) => p.present && p.regle) : [];
  const nomVoyageur = data
    ? [data.dossier.voyageur.nom.toUpperCase(), data.dossier.voyageur.prenom].filter(Boolean).join(' ') || 'Voyageur sans nom'
    : '';

  const carte = (p: PieceDossier) => (
    <PieceCarte key={p.id} piece={p} chemin={chemin} normeId={normeId} enCours={enCours.includes(p.id)} onConvertir={convertir}
      onRecadrer={(piece, page) => setRecadrage({ piece, page })} />
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6, flexWrap: 'wrap' }}>
          <Box sx={{ flexGrow: 1 }}>Normes des pièces jointes</Box>
          <Chip label={`Dossier : ${chemin}`} size="small" color="primary" variant="outlined" />
          {loading && data && <CircularProgress size={18} />}
          <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} aria-label="Fermer">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {erreur && (
            <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => charger(normeImposee)}>Réessayer</Button>}>
              {erreur}
            </Alert>
          )}
          {!data && loading && <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>}

          {data && (
            <>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
                <Box sx={{ flexGrow: 1, minWidth: 220 }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{nomVoyageur}</Typography>
                  <Typography variant="body2" color="text.secondary">Visa : {data.dossier.libelle_visa || '—'}</Typography>
                </Box>
                <TextField
                  select
                  size="small"
                  label="Norme appliquée"
                  sx={{ minWidth: 280 }}
                  value={normeImposee === null ? 'auto' : String(normeImposee)}
                  onChange={(e) => changerNorme(e.target.value)}
                  disabled={loading || toutEnCours}
                >
                  <MenuItem value="auto">
                    Automatique{normeImposee === null && data.norme ? ` (${data.norme.libelle})` : ''}
                  </MenuItem>
                  {data.normes_actives.map((n) => <MenuItem key={n.id} value={String(n.id)}>{n.libelle}</MenuItem>)}
                </TextField>
                <Chip size="small" color={ORIGINES[data.origine].color} label={ORIGINES[data.origine].label} />
                {data.candidates.length > 1 && (
                  <Tooltip title="Plusieurs normes correspondent à ce dossier : la première est appliquée, choisissez la bonne si besoin.">
                    <Chip size="small" color="warning" variant="outlined" label="plusieurs normes possibles" />
                  </Tooltip>
                )}
              </Box>

              {!data.norme && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Aucune norme ne correspond au visa « {data.dossier.libelle_visa || '—'} ». Choisissez-en une ci-dessus, ou
                  {menuNormes
                    ? ' rattachez ce visa (ou des mots-clés) à une norme dans le menu Pièces jointes.'
                    : ' demandez à VisaMonde de rattacher ce visa à une norme.'}
                </Alert>
              )}
              {manques.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Conversions partiellement indisponibles, outils manquants sur le serveur :
                  <Box component="ul" sx={{ m: 0, pl: 3 }}>{manques.map((m) => <li key={m}>{m}</li>)}</Box>
                </Alert>
              )}

              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Voyageur</Typography>
              {voyageur.map(carte)}

              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
                Pièces du formulaire ({formulaire.length})
              </Typography>
              {formulaire.length ? formulaire.map(carte) : (
                <Typography variant="body2" color="text.secondary">Aucune pièce jointe dans le dossier.</Typography>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          {data && data.norme && (
            <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto', ml: 1 }}>
              {avecRegle.filter(prete).length} / {avecRegle.length} pièce(s) prête(s) à déposer
            </Typography>
          )}
          <Button
            variant="contained"
            startIcon={toutEnCours ? <CircularProgress size={16} color="inherit" /> : <AutoFixHighIcon />}
            disabled={!aFaire.length || toutEnCours || loading}
            onClick={toutConvertir}
          >
            {toutEnCours ? 'Conversion en cours…' : `Tout convertir (${aFaire.length})`}
          </Button>
          <Button onClick={onClose}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {recadrage && (
        <RecadragePhotoDialog
          chemin={chemin}
          piece={recadrage.piece}
          titre={titrePiece(recadrage.piece)}
          normeId={normeId}
          page={recadrage.page}
          onClose={() => setRecadrage(null)}
          onConverti={(r) => {
            appliquer(recadrage.piece, r);
            setRecadrage(null);
          }}
        />
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={7000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? <Alert severity={toast.sev} onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </>
  );
};

export default ConformitePjDialog;
